import {
  allMantraBucketKeys,
  classicBucketsForPlayer,
  CLASSIC_GROUPS,
  mantraBucketsForPlayer,
} from './roleBuckets.js';

function isValidPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) && n >= 1;
}

function bucketsForPlayer(player, mode) {
  const fn = mode === 'classic' ? classicBucketsForPlayer : mantraBucketsForPlayer;
  return fn(player ?? { roles: [] });
}

function matchesRoleFilter(player, roleFilter, mode) {
  if (!roleFilter) return true;
  return bucketsForPlayer(player, mode).includes(roleFilter);
}

export const SERIES_ID_ME = 'me';
export const SERIES_ID_UNASSIGNED = 'unassigned';
/** Vista cronologica di tutta l'asta (tutte le chiamate in sequenza). */
export const SERIES_ID_GLOBAL = 'global';

export const TREND_VIEW_GLOBAL = 'global';
export const TREND_VIEW_ROSA = 'rosa';
export const TREND_VIEW_ROLES = 'roles';

const ROLE_MACRO_COLORS = {
  por: '#d97706',
  dif: '#16a34a',
  cen: '#1c4e80',
  att: '#dc2626',
};

function macroGroupForBucket(bucket) {
  const role = String(bucket || '').replace(/\+$/, '');
  if (role === 'P' || role === 'POR') return 'por';
  if (role === 'D' || ['DS', 'DC', 'DD', 'B'].includes(role)) return 'dif';
  if (['E', 'M', 'C', 'W', 'T'].includes(role)) return 'cen';
  if (role === 'A' || role === 'PC') return 'att';
  return 'cen';
}

export function colorForRoleBucket(bucket) {
  return ROLE_MACRO_COLORS[macroGroupForBucket(bucket)] || PRICE_TREND_SERIES_COLORS[0];
}

/** Palette dedicata al grafico «Confronta ruolo» — un colore per bucket, non per macro. */
export const ROLE_TREND_SERIES_COLORS = [
  '#d97706', '#f59e0b', '#16a34a', '#15803d', '#059669', '#0d9488',
  '#0891b2', '#2563eb', '#4f46e5', '#7c3aed', '#9333ea', '#c026d3',
  '#db2777', '#e11d48', '#dc2626', '#ea580c', '#ca8a04', '#65a30d',
  '#0e7490', '#1d4ed8', '#6d28d9', '#a21caf', '#be123c', '#b45309',
];

export function colorForRoleTrendSeries(bucket, mode = 'mantra') {
  const order = mode === 'classic' ? CLASSIC_GROUPS : allMantraBucketKeys();
  const idx = order.indexOf(bucket);
  if (idx < 0) return ROLE_TREND_SERIES_COLORS[0];
  return ROLE_TREND_SERIES_COLORS[idx % ROLE_TREND_SERIES_COLORS.length];
}

export const PRICE_TREND_SERIES_COLORS = [
  '#3d8bfd', // blu
  '#f0a202', // oro
  '#c44900', // ruggine
  '#7b2cbf', // viola
  '#db2777', // rosa acceso (lontano dal verde «Io»)
  '#e76f51', // salmone
  '#1e3a5f', // blu notte (lontano dal grigio «Da assegnare»)
];

const SERIES_COLOR_ME = '#0e8f4f';
const SERIES_COLOR_UNASSIGNED = '#78716c';

export function colorForSeriesId(id, opponentsOrderedIds = []) {
  if (id === SERIES_ID_ME) return SERIES_COLOR_ME;
  if (id === SERIES_ID_UNASSIGNED) return SERIES_COLOR_UNASSIGNED;
  const idx = opponentsOrderedIds.indexOf(id);
  if (idx >= 0) {
    return PRICE_TREND_SERIES_COLORS[idx % PRICE_TREND_SERIES_COLORS.length];
  }
  return PRICE_TREND_SERIES_COLORS[0];
}

export function collectMarketEvents({
  rosterEntries = [],
  exitEntries = [],
} = {}) {
  const rosterKeys = new Set(rosterEntries.map((entry) => entry.key));
  const events = [];

  for (const entry of rosterEntries) {
    if (!isValidPrice(entry.price)) continue;
    events.push({
      key: entry.key,
      price: Number(entry.price),
      at: Number(entry.at) || 0,
      mine: true,
    });
  }

  for (const entry of exitEntries) {
    if (rosterKeys.has(entry.key)) continue;
    if (!isValidPrice(entry.price)) continue;
    const event = {
      key: entry.key,
      price: Number(entry.price),
      at: Number(entry.at) || 0,
      mine: false,
    };
    if (entry.opponentId) {
      event.opponentId = entry.opponentId;
    }
    events.push(event);
  }

  return events;
}

export function groupMarketEventsByOwner(events) {
  const map = new Map();
  for (const event of events) {
    let ownerId;
    if (event.mine === true) {
      ownerId = SERIES_ID_ME;
    } else if (event.opponentId) {
      ownerId = event.opponentId;
    } else {
      ownerId = SERIES_ID_UNASSIGNED;
    }
    if (!map.has(ownerId)) map.set(ownerId, []);
    map.get(ownerId).push(event);
  }
  return map;
}

/**
 * Bucket ruolo presenti negli acquisti (per chip filtro grafico).
 */
export function listPriceTrendRoleBuckets({
  rosterEntries = [],
  exitEntries = [],
  playersByKey = new Map(),
  mode = 'mantra',
  marketEvents: marketEventsInput,
} = {}) {
  const order = mode === 'classic' ? CLASSIC_GROUPS : allMantraBucketKeys();
  const counts = new Map(order.map((bucket) => [bucket, 0]));
  const events = marketEventsInput ?? collectMarketEvents({ rosterEntries, exitEntries });

  for (const event of events) {
    const player = playersByKey.get(event.key);
    for (const bucket of bucketsForPlayer(player, mode)) {
      if (!counts.has(bucket)) counts.set(bucket, 0);
      counts.set(bucket, counts.get(bucket) + 1);
    }
  }

  return order
    .map((bucket) => ({ bucket, count: counts.get(bucket) || 0 }))
    .filter((row) => row.count > 0);
}

/**
 * Serie cronologica dei prezzi pagati (roster ∪ exits con prezzo).
 */
export function computePriceTrend({
  rosterEntries = [],
  exitEntries = [],
  playersByKey = new Map(),
  mode = 'mantra',
  roleFilter = null,
  marketEvents: marketEventsInput,
} = {}) {
  const events = (marketEventsInput ?? collectMarketEvents({ rosterEntries, exitEntries })).filter((event) => {
    const player = playersByKey.get(event.key);
    return matchesRoleFilter(player, roleFilter, mode);
  });

  events.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    return String(a.key).localeCompare(String(b.key), 'it');
  });

  let runningSum = 0;
  const points = events.map((event, index) => {
    runningSum += event.price;
    const player = playersByKey.get(event.key);
    return {
      index: index + 1,
      at: event.at,
      price: event.price,
      avg: Math.round(runningSum / (index + 1)),
      mine: event.mine,
      name: player?.name ?? event.key,
      buckets: bucketsForPlayer(player, mode),
    };
  });

  if (points.length === 0) {
    return { points: [], minPrice: 0, maxPrice: 0, roleFilter: roleFilter || null, totalTurns: 0 };
  }

  const prices = points.map((p) => p.price);
  return {
    points,
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
    roleFilter: roleFilter || null,
    totalTurns: points.length,
  };
}

/**
 * Ritaglia la serie globale su un intervallo di chiamate (turni asta).
 * Ricalcola la media cumulativa solo sul tratto visibile.
 */
export function slicePriceTrend(trend, fromTurn, toTurn) {
  const all = trend?.points || [];
  const totalTurns = trend?.totalTurns ?? all.length;
  if (!all.length) {
    return { ...trend, points: [], minPrice: 0, maxPrice: 0, totalTurns: 0 };
  }

  const maxTurn = all[all.length - 1].index;
  const from = Math.max(1, Math.min(Number(fromTurn) || 1, maxTurn));
  const to = Math.max(from, Math.min(Number(toTurn) || maxTurn, maxTurn));
  const sliced = all.filter((p) => p.index >= from && p.index <= to);

  if (!sliced.length) {
    return { ...trend, points: [], minPrice: 0, maxPrice: 0, totalTurns };
  }

  let runningSum = 0;
  const points = sliced.map((p, i) => {
    runningSum += p.price;
    return {
      ...p,
      turn: p.index,
      chartPos: i + 1,
      avg: Math.round(runningSum / (i + 1)),
    };
  });

  const prices = points.map((p) => p.price);
  return {
    ...trend,
    points,
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
    totalTurns,
    turnRange: { from, to },
  };
}

export const TURN_AXIS_STEP = 50;

/** Segnaposto asse X: estremi + multipli di 50 nel tratto visibile. */
export function buildTurnAxisTicks(minTurn, maxTurn, step = TURN_AXIS_STEP) {
  const min = Math.min(minTurn, maxTurn);
  const max = Math.max(minTurn, maxTurn);
  const ticks = new Set([min, max]);
  let v = Math.ceil(min / step) * step;
  if (v <= min) v += step;
  while (v < max) {
    ticks.add(v);
    v += step;
  }
  return [...ticks].sort((a, b) => a - b);
}

export function normalizeGlobalTurnRange(range, totalTurns) {
  const total = Math.max(1, Number(totalTurns) || 1);
  const fromRaw = Number(range?.from) || 1;
  const toRaw = Number(range?.to) || total;
  let from = Math.max(1, Math.min(fromRaw, total));
  let to = Math.max(1, Math.min(toRaw, total));
  if (from > to) [from, to] = [to, from];
  return { from, to, total };
}

/**
 * Serie andamento prezzi per rosa (me, avversario, unassigned).
 */
export function computePriceTrendSeries({
  rosterEntries = [],
  exitEntries = [],
  playersByKey = new Map(),
  mode = 'mantra',
  roleFilter = null,
  seriesIds = [],
} = {}) {
  const events = collectMarketEvents({ rosterEntries, exitEntries }).filter((event) => {
    const player = playersByKey.get(event.key);
    return matchesRoleFilter(player, roleFilter, mode);
  });

  const grouped = groupMarketEventsByOwner(events);

  const series = seriesIds.map((id) => {
    const groupEvents = grouped.get(id) ?? [];
    const trend = computePriceTrend({
      playersByKey,
      mode,
      marketEvents: groupEvents,
    });
    return { id, points: trend.points };
  });

  const maxIndex = Math.max(0, ...series.map((s) => s.points.length));
  const allPrices = series.flatMap((s) => s.points.map((p) => p.price));
  const minPrice = allPrices.length ? Math.min(...allPrices) : 0;
  const maxPrice = allPrices.length ? Math.max(...allPrices) : 0;

  return {
    series,
    maxIndex,
    minPrice,
    maxPrice,
    roleFilter: roleFilter || null,
  };
}

/**
 * Serie andamento prezzi per bucket ruolo (turni normalizzati per ruolo).
 */
export function computePriceTrendSeriesByRole({
  rosterEntries = [],
  exitEntries = [],
  playersByKey = new Map(),
  mode = 'mantra',
  roleSeriesIds = [],
} = {}) {
  const events = collectMarketEvents({ rosterEntries, exitEntries });
  const ids = Array.isArray(roleSeriesIds) ? roleSeriesIds.filter(Boolean) : [];

  const series = ids.map((bucket) => {
    const filtered = events.filter((event) => {
      const player = playersByKey.get(event.key);
      return bucketsForPlayer(player, mode).includes(bucket);
    });
    const trend = computePriceTrend({
      playersByKey,
      mode,
      marketEvents: filtered,
    });
    return { id: bucket, points: trend.points };
  });

  const maxIndex = Math.max(0, ...series.map((s) => s.points.length));
  const allPrices = series.flatMap((s) => s.points.map((p) => p.price));
  const minPrice = allPrices.length ? Math.min(...allPrices) : 0;
  const maxPrice = allPrices.length ? Math.max(...allPrices) : 0;

  return {
    series,
    maxIndex,
    minPrice,
    maxPrice,
    roleSeriesIds: ids,
  };
}

function niceStep(range, targetTicks = 4) {
  if (range <= 0) return 1;
  const rough = range / targetTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  let step = magnitude;
  if (residual > 5) step = 10 * magnitude;
  else if (residual > 2) step = 5 * magnitude;
  else if (residual > 1) step = 2 * magnitude;
  return Math.max(1, step);
}

function buildYTicks(yMin, yMax) {
  const step = niceStep(yMax - yMin);
  const start = Math.floor(yMin / step) * step;
  const ticks = [];
  for (let v = start; v <= yMax + step * 0.01; v += step) {
    if (v >= yMin - step * 0.01) ticks.push(Math.round(v));
  }
  if (!ticks.length) ticks.push(yMin, yMax);
  return ticks;
}

/**
 * SVG compatto per l'andamento prezzi (stile mini-grafico borsa).
 */
export function buildPriceTrendSvg(trendOrMulti, {
  width = 640,
  height = 200,
  opponentsOrderedIds,
  colorForSeries,
} = {}) {
  if (Array.isArray(trendOrMulti.series)) {
    const { series, maxIndex } = trendOrMulti;
    if (maxIndex === 0) return '';
    if (series.length === 1) {
      return buildSingleSeriesSvg(series[0].points, { width, height });
    }
    const opponentIds = opponentsOrderedIds ?? series
      .map((s) => s.id)
      .filter((id) => id !== SERIES_ID_ME && id !== SERIES_ID_UNASSIGNED);
    const resolveColor = colorForSeries
      || ((id) => colorForSeriesId(id, opponentIds));
    return buildMultiSeriesSvg(trendOrMulti, {
      width,
      height,
      colorForSeries: resolveColor,
    });
  }

  const { points } = trendOrMulti;
  if (!points?.length) return '';
  return buildSingleSeriesSvg(points, { width, height });
}

function buildSingleSeriesSvg(points, { width, height }) {
  if (!points.length) return '';

  const pad = { top: 14, right: 12, bottom: 26, left: 38 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const prices = points.map((p) => p.price);
  const avgs = points.map((p) => p.avg);
  const rawMin = Math.min(...prices, ...avgs);
  const rawMax = Math.max(...prices, ...avgs);
  const span = Math.max(1, rawMax - rawMin);
  const yPad = Math.max(2, span * 0.12);
  const yMin = Math.max(1, rawMin - yPad);
  const yMax = rawMax + yPad;
  const yTicks = buildYTicks(yMin, yMax);

  const xAtI = (posIndex) => {
    if (points.length === 1) return pad.left + plotW / 2;
    return pad.left + (posIndex / (points.length - 1)) * plotW;
  };
  const yAt = (value) =>
    pad.top + plotH - ((value - yMin) / (yMax - yMin)) * plotH;

  const pricePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAtI(i).toFixed(2)} ${yAt(p.price).toFixed(2)}`)
    .join(' ');

  const avgPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAtI(i).toFixed(2)} ${yAt(p.avg).toFixed(2)}`)
    .join(' ');

  const grid = yTicks
    .map((tick) => {
      const y = yAt(tick).toFixed(2);
      return `
        <line class="analytics-trend-grid" x1="${pad.left}" y1="${y}" x2="${width - pad.right}" y2="${y}" />
        <text class="analytics-trend-y-label" x="${pad.left - 6}" y="${y}" text-anchor="end" dominant-baseline="middle">${tick}</text>`;
    })
    .join('');

  const xLabels = buildXAxisLabels(points, xAtI, height);

  const dots = points
    .map((p, i) => {
      const cx = xAtI(i).toFixed(2);
      const cy = yAt(p.price).toFixed(2);
      const cls = p.mine ? 'analytics-trend-dot analytics-trend-dot--mine' : 'analytics-trend-dot analytics-trend-dot--others';
      const turn = p.turn ?? p.index;
      return `<circle class="${cls}" cx="${cx}" cy="${cy}" r="3.5"><title>Chiamata ${turn} · ${p.name} · ${p.price}M</title></circle>`;
    })
    .join('');

  return `
    <svg class="analytics-trend-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" role="img" aria-hidden="true">
      ${grid}
      <path class="analytics-trend-line analytics-trend-line--avg" d="${avgPath}" />
      <path class="analytics-trend-line analytics-trend-line--price" d="${pricePath}" />
      ${dots}
      ${xLabels}
    </svg>`;
}

function buildMultiSeriesSvg({ series, maxIndex, minPrice, maxPrice }, { width, height, colorForSeries }) {
  const pad = { top: 14, right: 12, bottom: 26, left: 38 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const span = Math.max(1, maxPrice - minPrice);
  const yPad = Math.max(2, span * 0.12);
  const yMin = Math.max(1, minPrice - yPad);
  const yMax = maxPrice + yPad;
  const yTicks = buildYTicks(yMin, yMax);

  const xAt = (index) => {
    if (maxIndex === 1) return pad.left + plotW / 2;
    return pad.left + ((index - 1) / (maxIndex - 1)) * plotW;
  };
  const yAt = (value) =>
    pad.top + plotH - ((value - yMin) / (yMax - yMin)) * plotH;

  const grid = yTicks
    .map((tick) => {
      const y = yAt(tick).toFixed(2);
      return `
        <line class="analytics-trend-grid" x1="${pad.left}" y1="${y}" x2="${width - pad.right}" y2="${y}" />
        <text class="analytics-trend-y-label" x="${pad.left - 6}" y="${y}" text-anchor="end" dominant-baseline="middle">${tick}</text>`;
    })
    .join('');

  const xLabels = (() => {
    if (maxIndex <= 1) {
      return `<text class="analytics-trend-x-label" x="${xAt(1).toFixed(2)}" y="${height - 8}" text-anchor="middle">1</text>`;
    }
    const maxLabels = 6;
    const step = Math.max(1, Math.ceil(maxIndex / maxLabels));
    const labels = [];
    for (let i = 1; i <= maxIndex; i += 1) {
      if (i === 1 || i === maxIndex || i % step === 0) {
        labels.push(
          `<text class="analytics-trend-x-label" x="${xAt(i).toFixed(2)}" y="${height - 8}" text-anchor="middle">${i}</text>`,
        );
      }
    }
    return labels.join('');
  })();

  const seriesPaths = series
    .filter((s) => s.points.length)
    .map((s) => {
      const color = colorForSeries(s.id);
      const path = s.points
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(p.index).toFixed(2)} ${yAt(p.price).toFixed(2)}`)
        .join(' ');
      const dots = s.points
        .map((p) => {
          const cx = xAt(p.index).toFixed(2);
          const cy = yAt(p.price).toFixed(2);
          return `<circle class="analytics-trend-dot analytics-trend-dot--series" cx="${cx}" cy="${cy}" r="2.5" fill="${color}" stroke="${color}"><title>${p.name} · ${p.price}M</title></circle>`;
        })
        .join('');
      return `
      <path class="analytics-trend-line analytics-trend-line--series" stroke="${color}" d="${path}" />
      ${dots}`;
    })
    .join('');

  return `
    <svg class="analytics-trend-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" role="img" aria-hidden="true">
      ${grid}
      ${seriesPaths}
      ${xLabels}
    </svg>`;
}

function buildXAxisLabels(points, xAtI, height) {
  if (!points.length) return '';
  const minTurn = points[0].turn ?? points[0].index ?? 1;
  const maxTurn = points[points.length - 1].turn ?? points[points.length - 1].index ?? points.length;

  if (points.length === 1) {
    return `<text class="analytics-trend-x-label" x="${xAtI(0).toFixed(2)}" y="${height - 8}" text-anchor="middle">${minTurn}</text>`;
  }

  const ticks = new Set(buildTurnAxisTicks(minTurn, maxTurn));
  const span = maxTurn - minTurn;

  return [...ticks]
    .sort((a, b) => a - b)
    .map((turn) => {
      const ratio = span === 0 ? 0 : (turn - minTurn) / span;
      const posIndex = ratio * (points.length - 1);
      const x = xAtI(posIndex).toFixed(2);
      return `<text class="analytics-trend-x-label" x="${x}" y="${height - 8}" text-anchor="middle">${turn}</text>`;
    })
    .join('');
}

function buildXLabels(points, xAt, height) {
  if (points.length <= 1) {
    const turn = points[0]?.turn ?? points[0]?.index ?? 1;
    const x = typeof xAt === 'function' && xAt.length === 1
      ? xAt(0).toFixed(2)
      : xAt(turn).toFixed(2);
    return `<text class="analytics-trend-x-label" x="${x}" y="${height - 8}" text-anchor="middle">${turn}</text>`;
  }
  const xAtI = typeof xAt === 'function' && xAt.length === 1
    ? xAt
    : (i) => xAt(points[i].index);
  return buildXAxisLabels(points, xAtI, height);
}
